import React, { useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { formatPrice } from "../../shared/utils/helper";
import areaIcon from "../../assets/images/icons/area.svg";
import { gsap } from "gsap";

interface LandItemProps {
  id: number;
  title: string;
  price: number;
  imageUrl: string;
  area?: number;
}

const LandItem: React.FC<LandItemProps> = ({
  id,
  title,
  price,
  imageUrl,
  area,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    const image = imageRef.current;
    const content = contentRef.current;
    const priceElement = priceRef.current;
    const titleElement = titleRef.current;

    if (!card || !image || !content || !priceElement || !titleElement) return;

    // Create the hover animation
    const enterAnimation = () => {
      // Image subtle zoom
      gsap.to(image, {
        scale: 1.07,
        duration: 0.7,
        ease: "power2.out",
      });

      // Text elements animation
      gsap.to(priceElement, {
        color: "#059669", // Darker emerald
        duration: 0.3,
        ease: "power1.inOut",
      });

      gsap.to(titleElement, {
        letterSpacing: "0.01em",
        duration: 0.4,
        ease: "power1.out",
      });
    };

    // Create the leave animation
    const leaveAnimation = () => {
      // Image reset
      gsap.to(image, {
        scale: 1,
        duration: 0.7,
        ease: "power2.out",
      });

      // Text elements reset
      gsap.to(priceElement, {
        color: "#10b981", // Original emerald color
        duration: 0.3,
        ease: "power1.inOut",
      });

      gsap.to(titleElement, {
        letterSpacing: "0",
        duration: 0.4,
        ease: "power1.out",
      });
    };

    // Add event listeners
    card.addEventListener("mouseenter", enterAnimation);
    card.addEventListener("mouseleave", leaveAnimation);

    // Handle cleanup for cursor state
    const handleClick = () => {
      // Ensure GSAP animations are properly cleaned up
      gsap.killTweensOf(image);
      gsap.killTweensOf(priceElement);
      gsap.killTweensOf(titleElement);

      // Reset to initial state
      leaveAnimation();
    };

    card.addEventListener("click", handleClick);

    // Cleanup function
    return () => {
      card.removeEventListener("mouseenter", enterAnimation);
      card.removeEventListener("mouseleave", leaveAnimation);
      card.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <NavLink to={`/land-for-sale/land-${id}`} className="block land-item-link">
      <div
        ref={cardRef}
        className="w-full h-auto overflow-hidden rounded-[6px] bg-gray-50"
      >
        {/* Image container */}
        <div className="relative w-full h-[334px] overflow-hidden">
          <img
            ref={imageRef}
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content section */}
        <div ref={contentRef} className="p-4">
          {/* Price */}
          <div
            ref={priceRef}
            className="text-sm font-bold text-emerald-600 font-sans"
          >
            {formatPrice(price)}
          </div>

          {/* Title */}
          <h3
            ref={titleRef}
            className="pt-2 text-3xl font-bold font-title italic text-neutral-800 title font-italic"
          >
            {title}
          </h3>

          {/* Area with icon */}
          {area && (
            <div className="flex items-center mt-3 text-gray-600 font-sans font-bold">
              <img src={areaIcon} alt="Area" className="w-5 h-5 mr-2" />
              <span>{area} m²</span>
            </div>
          )}
        </div>
      </div>
    </NavLink>
  );
};

export default LandItem;
